import React, { useState } from 'react';
import { Tooltip, FormFeedback } from 'reactstrap';

const RangePickerInput = ({
    label = "Select Range",
    required = false,
    start = 0,
    end = 100,
    min = 20,
    max = 80,
    step = 1,
    id,
    requiredErrorMessage = "Please select a valid range.",
    outOfBoundsErrorMessage = `Please select a range between ${min} and ${max}.`,
    helpText = "Please select a valid range.",
    showHelp = false,
    onChange = () => {},
    color = '#007bff', // Default color for the selected range
}) => {
    const [minRange, setMinRange] = useState(min);
    const [maxRange, setMaxRange] = useState(max);
    const [error, setError] = useState("");
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleRangeChange = (e) => {
        const value = parseFloat(e.target.value);
        const diffMin = Math.abs(value - minRange);
        const diffMax = Math.abs(value - maxRange);

        // Determine which handle to move based on which is closer to the clicked point
        if (diffMin < diffMax && value < maxRange) {
            // Move the min handle if it's closer and valid
            setMinRange(value);
            validateRange(value, maxRange);
            onChange(value, maxRange);
        } else if (diffMax < diffMin && value > minRange) {
            // Move the max handle if it's closer and valid
            setMaxRange(value);
            validateRange(minRange, value);
            onChange(minRange, value);
        }
    };

    const validateRange = (minValue, maxValue) => {
        if (required && (minValue < start || maxValue > end || minValue >= maxValue)) {
            setError(requiredErrorMessage);
        } else if (minValue < min || maxValue > max) {
            setError(outOfBoundsErrorMessage);
        } else {
            setError("");
        }
    };

    // Calculate the left and right percentage positions for the dark range
    const selectedRangeStart = `${((minRange - start) / (end - start)) * 100}%`;
    const selectedRangeEnd = `${((maxRange - start) / (end - start)) * 100}%`;

    return (
        <div className="mb-3">
            <label className="d-flex align-items-center">
                {label}
                {required && <span className="text-danger ml-1">*</span>}
                {showHelp && (
                    <span id={`helpTooltip-${id}`} className="px-2">
                        <i
                            className="ri-question-line"
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                        ></i>
                        <Tooltip
                            placement="right"
                            isOpen={tooltipOpen}
                            target={`helpTooltip-${id}`}
                            toggle={() => setTooltipOpen(!tooltipOpen)}
                            style={{ backgroundColor: 'black' }}
                        >
                            {helpText}
                        </Tooltip>
                    </span>
                )}
            </label>
            <div style={{ position: 'relative', marginTop: '8px', width: '100%' }}>
                {/* Full Range Bar */}
                <div style={{ height: '6px', backgroundColor: '#ddd', borderRadius: '5px', position: 'relative' }}>
                    {/* Dark Colored Range (selected range) */}
                    <div
                        style={{
                            position: 'absolute',
                            left: selectedRangeStart,
                            right: `calc(100% - ${selectedRangeEnd})`,
                            height: '100%',
                            backgroundColor: color,  // Use the passed color prop for the selected range
                            borderRadius: '5px',
                        }}
                    />
                    {/* Min handle */}
                    <div
                        style={{
                            position: 'absolute',
                            left: `${((minRange - start) / (end - start)) * 100}%`,
                            transform: 'translate(-50%)',
                            top: '-10px',
                            backgroundColor: color,
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                        }}
                    >
                        {minRange.toFixed(1)}
                    </div>
                    {/* Max handle */}
                    <div
                        style={{
                            position: 'absolute',
                            left: `${((maxRange - start) / (end - start)) * 100}%`,
                            transform: 'translate(-50%)',
                            top: '-10px',
                            backgroundColor: color,
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                        }}
                    >
                        {maxRange.toFixed(1)}
                    </div>
                </div>
                {/* Range sliders (transparent inputs) */}
                <input
                    type="range"
                    min={start}
                    max={end}
                    step={step}
                    value={minRange}
                    onChange={handleRangeChange}
                    style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        opacity: 0,
                        pointerEvents: 'all',
                        zIndex: 2,
                        cursor: 'pointer',
                    }}
                />
                <input
                    type="range"
                    min={start}
                    max={end}
                    step={step}
                    value={maxRange}
                    onChange={handleRangeChange}
                    style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        opacity: 0,
                        pointerEvents: 'all',
                        zIndex: 1,
                        cursor: 'pointer',
                    }}
                />
            </div>
            {error && <FormFeedback className="d-block mt-2">{error}</FormFeedback>}
        </div>
    );
};

export default RangePickerInput;
